import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { 
  KeyIcon, 
  EyeIcon, 
  EyeSlashIcon,
  ArrowLeftIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';

const CambiaPasswordPage = () => {
  const navigate = useNavigate();
  const [passwordData, setPasswordData] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Gestione cambio password
  const handlePasswordChange = async (e) => {
    e.preventDefault();
    
    if (passwordData.newPassword !== passwordData.confirmPassword) {
      toast.error('Le password non corrispondono');
      return;
    }

    if (passwordData.newPassword.length < 6) {
      toast.error('La password deve essere di almeno 6 caratteri');
      return;
    }

    setIsLoading(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch('/api/auth/cambia-password', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          currentPassword: passwordData.currentPassword,
          newPassword: passwordData.newPassword
        })
      });

      if (response.ok) {
        toast.success('Password cambiata con successo!');
        setPasswordData({
          currentPassword: '',
          newPassword: '',
          confirmPassword: ''
        });
        // Redirect al profilo dopo il successo
        setTimeout(() => {
          navigate('/profilo');
        }, 2000);
      } else {
        const error = await response.json();
        toast.error(error.message || 'Errore nel cambio password');
      }
    } catch (error) {
      console.error('Errore cambio password:', error);
      toast.error('Errore di connessione');
    } finally {
      setIsLoading(false);
    }
  };

  // Validazione in tempo reale
  const isPasswordValid = passwordData.newPassword.length >= 6;
  const doPasswordsMatch = passwordData.newPassword === passwordData.confirmPassword && passwordData.confirmPassword !== '';
  const isFormValid = passwordData.currentPassword && passwordData.newPassword && passwordData.confirmPassword && isPasswordValid && doPasswordsMatch;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Contenuto principale */}
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Pulsante Indietro */}
        <div className="mb-6">
          <button
            onClick={() => navigate(-1)}
            className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
          >
            <ArrowLeftIcon className="h-4 w-4 mr-1" />
            Indietro
          </button>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Modifica Password</h2>
            <p className="text-sm text-gray-600 mt-1">
              Inserisci la tua password attuale e la nuova password che desideri utilizzare
            </p>
          </div>

          <form onSubmit={handlePasswordChange} className="px-6 py-6">
            <div className="space-y-6">
              {/* Password attuale */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Password Attuale *
                </label>
                <div className="relative">
                  <input
                    type={showCurrentPassword ? "text" : "password"}
                    value={passwordData.currentPassword}
                    onChange={(e) => setPasswordData({...passwordData, currentPassword: e.target.value})}
                    className="w-full px-3 py-3 pr-10 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                    placeholder="Inserisci la password attuale"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 transition-colors"
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  >
                    {showCurrentPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
              </div>

              {/* Nuova password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nuova Password *
                </label>
                <div className="relative">
                  <input
                    type={showNewPassword ? "text" : "password"}
                    value={passwordData.newPassword}
                    onChange={(e) => setPasswordData({...passwordData, newPassword: e.target.value})}
                    className={`w-full px-3 py-3 pr-10 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-blue-500 transition-all ${
                      passwordData.newPassword && !isPasswordValid 
                        ? 'border-red-300 focus:ring-red-500' 
                        : passwordData.newPassword && isPasswordValid 
                          ? 'border-green-300 focus:ring-green-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="Inserisci la nuova password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 transition-colors"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                  >
                    {showNewPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {passwordData.newPassword && (
                  <div className="mt-2 flex items-center text-sm">
                    {isPasswordValid ? (
                      <div className="flex items-center text-green-600">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Password valida
                      </div>
                    ) : (
                      <div className="flex items-center text-red-600">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        Minimo 6 caratteri richiesti
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Conferma password */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Conferma Nuova Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    value={passwordData.confirmPassword}
                    onChange={(e) => setPasswordData({...passwordData, confirmPassword: e.target.value})}
                    className={`w-full px-3 py-3 pr-10 border rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:border-blue-500 transition-all ${
                      passwordData.confirmPassword && !doPasswordsMatch 
                        ? 'border-red-300 focus:ring-red-500' 
                        : passwordData.confirmPassword && doPasswordsMatch 
                          ? 'border-green-300 focus:ring-green-500' 
                          : 'border-gray-300 focus:ring-blue-500'
                    }`}
                    placeholder="Conferma la nuova password"
                    required
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center hover:text-gray-600 transition-colors"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <EyeSlashIcon className="h-5 w-5 text-gray-400" />
                    ) : (
                      <EyeIcon className="h-5 w-5 text-gray-400" />
                    )}
                  </button>
                </div>
                {passwordData.confirmPassword && (
                  <div className="mt-2 flex items-center text-sm">
                    {doPasswordsMatch ? (
                      <div className="flex items-center text-green-600">
                        <CheckCircleIcon className="h-4 w-4 mr-1" />
                        Le password corrispondono
                      </div>
                    ) : (
                      <div className="flex items-center text-red-600">
                        <ExclamationTriangleIcon className="h-4 w-4 mr-1" />
                        Le password non corrispondono
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Requisiti password */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h4 className="text-sm font-medium text-blue-900 mb-2 flex items-center">
                  <KeyIcon className="h-4 w-4 mr-2" />
                  Requisiti Password
                </h4>
                <ul className="text-sm text-blue-800 space-y-1">
                  <li className="flex items-center">
                    <div className={`w-2 h-2 rounded-full mr-2 ${isPasswordValid ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                    Almeno 6 caratteri
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 rounded-full mr-2 bg-blue-500"></div>
                    Combinazione di lettere e numeri consigliata
                  </li>
                  <li className="flex items-center">
                    <div className="w-2 h-2 rounded-full mr-2 bg-blue-500"></div>
                    Evita di usare informazioni personali
                  </li>
                </ul>
              </div>

              {/* Pulsanti azione */}
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => navigate(-1)}
                  className="px-6 py-3 border border-gray-300 shadow-sm text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                >
                  Annulla
                </button>
                <button
                  type="submit"
                  disabled={!isFormValid || isLoading}
                  className="px-6 py-3 border border-transparent shadow-sm text-sm font-medium rounded-lg text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isLoading ? (
                    <div className="flex items-center">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      Aggiornamento...
                    </div>
                  ) : (
                    <div className="flex items-center">
                      <KeyIcon className="h-4 w-4 mr-2" />
                      Cambia Password
                    </div>
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>

        {/* Consigli di sicurezza */}
        <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-4">
          <h3 className="text-sm font-medium text-yellow-800 mb-2">💡 Consigli per una password sicura</h3>
          <ul className="text-sm text-yellow-700 space-y-1">
            <li>• Usa una combinazione di lettere maiuscole e minuscole</li>
            <li>• Includi numeri e caratteri speciali</li>
            <li>• Non utilizzare la stessa password per più account</li>
            <li>• Cambia la password regolarmente</li>
            <li>• Non condividere mai la tua password con altri</li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default CambiaPasswordPage; 